import { Injectable } from '@angular/core';
import API, { graphqlOperation } from '@aws-amplify/api';
import {
  KeyStringFilterInput,
  StringFilterInput,
} from 'app/api/common.api.types';
import { EMPTY, from, Observable } from 'rxjs';
import { concatAll, expand, map, toArray, tap } from 'rxjs/operators';

export interface PayrollExtraFilterInput {
  companyId: StringFilterInput;
  name: StringFilterInput;
  key: StringFilterInput;
}

export interface PayrollExtraEntry {
  companyId: string;
  name: string;
  key: string;
  id?: string;
  applicable?: boolean;
  combobox?: boolean;
  choices?: string;
}

export interface CreatePayrollExtraEntry {
  companyId: string;
  name: string;
  applicable: boolean;
  combobox: boolean;
}

export interface DeletePayrollExtraInput {
  companyId: string;
  key: string;
}

export interface PayrollExtraConnection {
  items: PayrollExtraEntry[];
  lastEvaluatedKey: any;
}

@Injectable({
  providedIn: 'root',
})
export class PayrollExtraAPIService {
  PayrollExtra(
    companyId: string,
    key?: KeyStringFilterInput,
    filter?: PayrollExtraFilterInput,
    limit?: number
  ): Observable<PayrollExtraEntry[]> {
    const statement = `query PayrollExtra($id: String!, $key: KeyStringFilterInput, $filter: PayrollExtraFilterInput, $limit: Int, $exclusiveStartKey: AWSJSON) {
      company(id: $id) {
        payrollExtra(key: $key, filter: $filter, limit: $limit, exclusiveStartKey: $exclusiveStartKey) {
          items {
            companyId
            key
            name
            applicable
            combobox
            choices
          }
          lastEvaluatedKey
        }
      }
    }`;
    const gqlAPIServiceArguments: any = {};
    gqlAPIServiceArguments.id = companyId;
    if (key) {
      gqlAPIServiceArguments.key = key;
    }
    if (filter) {
      gqlAPIServiceArguments.filter = filter;
    }
    if (limit) {
      gqlAPIServiceArguments.limit = limit;
    }

    return this.performQuery(statement, gqlAPIServiceArguments).pipe(
      expand((conn) => {
        const exclusiveStartKey = conn.lastEvaluatedKey;
        const args = { ...gqlAPIServiceArguments, exclusiveStartKey };
        return conn.lastEvaluatedKey && JSON.parse(conn.lastEvaluatedKey) !== ''
          ? this.performQuery(statement, args)
          : EMPTY;
      }),
      map((conn) => conn.items),
      concatAll(),
      toArray()
    );
  }

  CreatePayrollExtraEntry(
    input: CreatePayrollExtraEntry
  ): Observable<PayrollExtraEntry> {
    const statement = `mutation CreatePayrollExtraEntry($input: CreatePayrollExtraEntryInput!) {
          createPayrollExtraEntry(input: $input) {
            companyId
            key
            name
            applicable
            combobox
          }
        }`;
    const gqlAPIServiceArguments: any = {
      input,
    };

    return from(
      API.graphql(graphqlOperation(statement, gqlAPIServiceArguments))
    ).pipe(
      map(
        (response: any): PayrollExtraEntry => {
          return response.data.createPayrollExtraEntry;
        }
      )
    );
  }

  UpdatePayrollExtraEntry(
    input: PayrollExtraEntry
  ): Observable<PayrollExtraEntry> {
    const statement = `mutation UpdatePayrollExtraEntry($input: UpdatePayrollExtraEntryInput!) {
          updatePayrollExtraEntry(input: $input) {
            companyId
            name
            key
            id
            applicable
            combobox
            choices
          }
        }`;
    const gqlAPIServiceArguments: any = {
      input,
    };
    return from(
      API.graphql(graphqlOperation(statement, gqlAPIServiceArguments))
    ).pipe(
      map(
        (response: any): PayrollExtraEntry => {
          return response.data.updatePayrollExtraEntry;
        }
      )
    );
  }

  DeletePayrollExtraEntry(
    input: DeletePayrollExtraInput
  ): Observable<PayrollExtraEntry> {
    const statement = `mutation DeletePayrollExtraEntry($input: DeletePayrollExtraEntryInput!) {
      deletePayrollExtraEntry(input: $input) {
        companyId
        name
        key
        id
        applicable
        combobox
        choices
      }
    }`;
    const gqlAPIServiceArguments: any = {
      input
    };

    return from(API.graphql(
      graphqlOperation(statement, gqlAPIServiceArguments)
    )).pipe(
      map((response: any): PayrollExtraEntry => {
        console.log(response);
        return response.data.deletePayrollExtraEntry;
      })
    );
  }

  private performQuery(
    statement: string,
    args: any = {}
  ): Observable<PayrollExtraConnection> {
    return from(API.graphql(graphqlOperation(statement, args))).pipe(
      map((response: any) => {
        return response.data.company.payrollExtra as PayrollExtraConnection;
      })
    );
  }
}
